import * as React from 'react';
import {ErrorResponse, Item} from '../../api/dtos/dtos';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../../app/ApplicationContext';
import {ErrorAlert} from '../../common/ErrorAlert';
import {ColumnProps} from 'antd/lib/table';
import {idRowKeyFn, simpleColumn} from '../../utils/table-utils';
import locales from '../../locales';
import {Link} from 'react-router-dom';
import * as paths from '../../paths';
import {renderItemName} from '../../item/item-name-renderer';
import {Button, Modal, Table} from 'antd';
import ButtonBar from '../../common/ButtonBar';
import ItemSelector from '../../item/ItemSelector';
import furoApi from '../../api/FuroApi';
import ButtonGroup from 'antd/lib/button/button-group';
import {showConfirm} from '../../utils/dialogs';
import {RentalState} from '../../common/state';

interface Props {
  disabled: boolean;
  incomingInvoiceId: number;
}

interface State {
  itemSelectorVisible: boolean;
  error?: ErrorResponse;
  items: Item[];
}

class InvoiceItemsEditor extends React.Component<Props, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: State = {
    itemSelectorVisible: false,
    items: [],
  };

  public componentDidMount() {
    this.loadInvoiceItems();
  }

  public render() {
    const {items, itemSelectorVisible, error} = this.state;
    const excludedItemsFromSearch: Item[] = items;

    const columns: Array<ColumnProps<Item>> = [
      simpleColumn(locales.item, i => <Link to={paths.viewItemPath(i.id)}>{renderItemName(i)}</Link>),
      simpleColumn(locales.inventoryNumber, i => i.inventoryNumber),
      simpleColumn(locales.serial, i => i.serial),
      simpleColumn(locales.category, i => i.category),
      simpleColumn(locales.comment, i => i.comment),
      {key: 'actions', width: 48, render: this.renderActionColumn}
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <ButtonBar style={{marginBottom: 8}}>
          <Button size="small" type="primary" icon="plus"
                  onClick={this.showItemSelector}>{locales.add}</Button>
        </ButtonBar>

        <Modal
          title={locales.select_item}
          visible={itemSelectorVisible}
          footer={null}
          onCancel={this.hideItemSelector}
          width="auto"
          style={{margin: '0 24px'}}
          destroyOnClose={true}
        >
          <ItemSelector
            allowedStates={[RentalState.AVAILABLE, RentalState.RENTED]}
            selectedStates={[RentalState.AVAILABLE, RentalState.RENTED]}
            excludedFromSearch={excludedItemsFromSearch}
            onSelect={this.addItemToInvoice}/>
        </Modal>

        <Table
          style={{marginBottom: 24}}
          columns={columns}
          dataSource={items}
          rowKey={idRowKeyFn}
          bordered={true}
          pagination={false}
          size="small"
          scroll={{x: 1024}}
        />

        <ErrorAlert style={{marginTop: '16px'}} error={error}/>
      </>
    );
  }

  private renderActionColumn = (text: any, item: Item, index: number) => {
    const {disabled} = this.props;
    const onDeleteItemClicked = () => {
      showConfirm(locales.incoming_invoice_item_detach_confirm, () => this.removeItemFromInvoice(item));
    };
    return (
      <div className="action-column" style={{textAlign: 'right'}}>
        <ButtonGroup>
          <Button
            icon="delete"
            disabled={disabled}
            onClick={onDeleteItemClicked}
            title={locales.detach}
          />
        </ButtonGroup>
      </div>
    );
  };

  private showItemSelector = () => {
    this.setState({itemSelectorVisible: true});
  };

  private hideItemSelector = () => {
    this.setState({itemSelectorVisible: false});
  };

  private addItemToInvoice = (item: Item) => {
    this.hideItemSelector();
    const {incomingInvoiceId} = this.props;
    this.performApiCall(furoApi.incomingInvoices.addItem(incomingInvoiceId, item.id!!));
  };

  private removeItemFromInvoice = (item: Item) => {
    const {incomingInvoiceId} = this.props;
    this.performApiCall(furoApi.incomingInvoices.removeItem(incomingInvoiceId, item.id!!))
  };

  private loadInvoiceItems = () => {
    const {incomingInvoiceId} = this.props;
    this.performApiCall(furoApi.incomingInvoices.getItems(incomingInvoiceId));
  };

  private performApiCall = (itemsPromise: Promise<Item[]>) => {
    const promise = itemsPromise
      .then((items) => this.setState({items, error: undefined}))
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onApiError = (error: ErrorResponse) => this.setState({error});
}

export default InvoiceItemsEditor;


// WEBPACK FOOTER //
// ./src/incoming-invoices/details/InvoiceItemsEditor.tsx